import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  StackProps,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { Image } from '@common/components/Image';
import { OtpInput } from '@common/elements/OtpInput';
import { isValidEmail, isValidPassword } from '@common/utility/validations';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';
import { useSignInViewModel } from '../../models/SignInViewModel';

interface OtpLabelProps {
  codeResent: boolean;
  mfaDeliveryDestination?: string;
  isConfirmChallenge: boolean;
  isVerifyingCode: boolean;
}

const OtpLabel: React.FC<OtpLabelProps> = ({
  codeResent,
  mfaDeliveryDestination,
  isConfirmChallenge,
  isVerifyingCode,
}) => {
  if (isVerifyingCode) {
    return <FormLabel>please wait...</FormLabel>;
  }
  return (
    <>
      {isConfirmChallenge && (
        <FormLabel>Your account was never confirmed. Let's do that before you sign in for the first time.</FormLabel>
      )}
      <FormLabel>
        We've {codeResent ? 'resent the' : 'sent a'} code to{` `}
        <strong>{mfaDeliveryDestination}</strong>.{` `}
        Enter it below to verify your phone number.{' '}
      </FormLabel>
    </>
  );
};

export const SignInForm: React.FC<StackProps> = (props: StackProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isQuestionsOpen, setIsQuestionsOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { isOpen: otpModalIsOpen, onOpen: openOTPmodal, onClose: closeOTPmodal } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { signIn, verifySignInCode, mfaDeliveryDestination } = useSignInViewModel();
  const credentialsForm = useForm<{ email: string; password: string }>();
  const codeForm = useForm<{ code: string }>();
  const [codeResent, setCodeResent] = useState(false);
  const [otp, setOtp] = useState<string>('');
  const [isConfirmChallenge, setIsConfirmChallenge] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleCredentialsSubmission = credentialsForm.handleSubmit(async (data) => {
    setIsLoading(true);
    const result = await signIn(data.email, data.password);
    if (result.error) {
      setPageError(result.error);
    } else {
      setPageError(null);
      if (result.challenge) {
        // open otp if there's a challenge
        setIsConfirmChallenge(Boolean(result.isConfirmChallenge));
        openOTPmodal();
      } else {
        navigate(result.signInRoute || ROUTES.dashboard);
      }
    }
    setIsLoading(false);
  });

  const handleOtpInput = (value: string) => {
    setOtp(value);
    if (value.length > 5) {
      handleCodeSubmission(value);
    }
  };

  const handleCodeSubmission = async (code: string) => {
    setIsVerifyingCode(true);
    codeForm.clearErrors();
    const result = await verifySignInCode(code, isConfirmChallenge, credentialsForm.getValues('email'));
    setIsVerifyingCode(false);
    if (result.error) {
      codeForm.setError('code', { message: result.error });
      codeForm.resetField('code');
    } else {
      if (result.confirmedSignUp) {
        // Require the user to sign in again after confirming.
        closeOTPmodal();
        setOtp('');
        setIsConfirmChallenge(false);
        navigate(ROUTES.signin, {
          replace: true,
          state: { confirmedSignUp: true },
        });
      } else {
        navigate(result.signInRoute || ROUTES.dashboard);
      }
    }
  };

  const handleResendCode = async () => {
    setCodeResent(true);
    closeOTPmodal();
    handleCredentialsSubmission();
  };

  return (
    <Stack spacing="8" {...props}>
      {/* Questions Modal */}
      <Modal isCentered isOpen={isQuestionsOpen} onClose={() => setIsQuestionsOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Questions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={'20px'} alignItems={'flex-start'}>
              <VStack>
                <TextBlock weight="bold" lineHeight={1}>
                  Do I need to sign up again if I already have an account in the mobile app?
                </TextBlock>
                <TextBlock lineHeight={1}>No, your mobile app credentials work in the web app as well.</TextBlock>
              </VStack>
              <VStack alignItems={'flex-start'}>
                <TextBlock weight="bold" lineHeight={1}>
                  I'm having trouble signing in. What should I do?
                </TextBlock>
                <TextBlock lineHeight={1}>
                  If you're unable to access your account, verify that your credentials are correct or try a{' '}
                  <Link href={ROUTES.resetPassword}>password reset</Link>. If all else fails, use the contact
                  information provided at{' '}
                  <Link href="https://concreit.com" target="_blank">
                    concreit.com
                  </Link>
                </TextBlock>
              </VStack>
              <VStack>
                <TextBlock weight="bold" lineHeight={1}>
                  I'm not receiving the verification code. What should I do?
                </TextBlock>
                <TextBlock lineHeight={1}>
                  Carriers can sometimes block verification codes sent over text. Waiting for a short period before
                  retrying can sometimes resolve the issue. If the issue persists you may need to contact us so we can
                  verify that the phone number is correct or you may need to contact your carrier to resolve the issue.
                </TextBlock>
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="blue" mr={3} onClick={() => setIsQuestionsOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Verification Code Form */}
      <Modal
        isCentered
        closeOnOverlayClick={false}
        isOpen={otpModalIsOpen}
        onClose={closeOTPmodal}
        motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isVerifyingCode ? 'Verifying' : 'Enter Code'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack>
              <OtpLabel
                codeResent={codeResent}
                mfaDeliveryDestination={mfaDeliveryDestination}
                isConfirmChallenge={isConfirmChallenge}
                isVerifyingCode={isVerifyingCode}
              />
              <Stack w="full">
                <FormControl isInvalid={Boolean(codeForm.formState.errors.code)}>
                  {isVerifyingCode ? (
                    <Progress isIndeterminate size="xs" />
                  ) : (
                    <OtpInput otp={otp} onChange={handleOtpInput} />
                  )}
                  <FormErrorMessage>
                    {codeForm.formState.errors.code && codeForm.formState.errors.code.message}
                  </FormErrorMessage>
                </FormControl>
              </Stack>
            </VStack>
          </ModalBody>

          <ModalFooter gap="1">
            <Button variant="ghost" colorScheme={'gray'} onClick={closeOTPmodal} size="sm">
              Cancel
            </Button>
            <Button variant="ghost" colorScheme={'blue'} onClick={handleResendCode} size="sm">
              Resend Code
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Left Column */}
      <Stack spacing="6">
        {isMobile && (
          <Flex align="center" justify={'center'} mb={20}>
            <Image image="logo-black" w={150} />
          </Flex>
        )}
        <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
          <Heading size={['md', 'lg']}>Sign in to your account</Heading>
          <HStack spacing="1" justify="center">
            <TextBlock color="muted">Don't have an account?</TextBlock>
            <Button as={Link} href={ROUTES.signup} variant="link">
              Sign up
            </Button>
          </HStack>
          {pageError && !isLoading && (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>{pageError}</AlertTitle>
            </Alert>
          )}
          {location.state?.confirmedSignUp && (
            <Alert status="success">
              <AlertIcon />
              <AlertTitle>Account confirmed successfully! You can now sign in with your credentials.</AlertTitle>
              <IconButton aria-label="Close alert" icon={<Icon icon="times" />} size="sm" variant="ghost" ml="auto" />
            </Alert>
          )}
        </Stack>
      </Stack>

      {/* Credentials Form */}
      <form onSubmit={handleCredentialsSubmission}>
        <Stack spacing="6">
          <Stack spacing="5">
            <FormControl isInvalid={Boolean(credentialsForm.formState.errors.email)}>
              <FormLabel htmlFor="email">Email Address</FormLabel>
              <Input
                id="email"
                placeholder="me@example.com"
                {...credentialsForm.register('email', {
                  required: 'Please enter an email',
                  validate: (v) => isValidEmail(v) || 'Please use a valid email address',
                })}
                autoFocus={true}
              />
              <FormErrorMessage>{credentialsForm.formState.errors.email?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(credentialsForm.formState.errors.password)}>
              <FormLabel htmlFor="password">Password</FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  autoComplete="off"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  {...credentialsForm.register('password', {
                    minLength: { message: 'Password must be at least 8 characters', value: 8 },
                    required: 'Password is required',
                    validate: (v) =>
                      isValidPassword(v) ||
                      'Password must be at least 8 characters, contain a number, and a special character',
                  })}
                />
                <InputRightElement mx="1.5">
                  <IconButton
                    aria-label="Show hide password"
                    bg="transparent"
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    color="grey"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={<Icon icon={showPassword ? 'eye' : 'eye-slash'} />}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{credentialsForm.formState.errors.password?.message}</FormErrorMessage>
            </FormControl>
          </Stack>

          <Stack spacing="4">
            <Button
              isDisabled={
                Boolean(credentialsForm.formState.errors.email) ||
                Boolean(credentialsForm.formState.errors.password) ||
                isLoading
              }
              isLoading={isLoading}
              type="submit">
              Continue
            </Button>
            <HStack justifyContent={'center'} alignItems={'center'} gap="20px">
              <Button variant="link" colorScheme="blue" size="sm" onClick={() => setIsQuestionsOpen(true)}>
                Questions?
              </Button>{' '}
              <Button variant="link" colorScheme="blue" size="sm" onClick={() => navigate(ROUTES.resetPassword)}>
                Forgot password
              </Button>
            </HStack>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};
